import React, { useState, useEffect, useRef } from 'react';
import Card from './card';
import { isTabletLandscape } from '../../../helpers/breakpoints';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import {
  RequestAQuoteFormProps,
  CardProps
} from '../../components/request-a-quote-form/types';

import RequestAQuoteForm from '../../components/request-a-quote-form';
import { ExtendedWindowType } from '../../engine';
import { createDataLayerString } from '../../../helpers/helpers';

const ContractorRequestAForm: React.FC<RequestAQuoteFormProps> = ({
  contractorCard,
  formLabels,
  callUsLabels,
  contractor,
  ...props
}) => {
  const { heading } = formLabels ? JSON.parse(formLabels) : '';
  const callUsCardLabels: CardProps = callUsLabels
    ? JSON.parse(callUsLabels)
    : '';
  const contractorData = contractor ? JSON.parse(contractor as string) : '';

  const [sticky, setSticky] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [fixedHeight, setFixedHeight] = useState<number>(0);
  const [cardHeight, setCardHeight] = useState<number>(0);
  const card = useRef<HTMLDivElement | null>(null);
  const styleRequestAQuote: { [key: string]: string } = {
    '--request-a-quote-fixed': `${fixedHeight}px`,
    '--request-a-quote-card-height': `${cardHeight}px`
  };

  const handleScroll = () => {
    const rect = (card.current as HTMLElement)?.getBoundingClientRect();
    if (rect.top <= rect.height * -1) {
      if (!sticky) {
        setSticky(true);
        const global = window as ExtendedWindowType;
        if (
          (global.dataLayer as { event_action: string }[]).findIndex(
            item => item.event_action === 'sticky request a quote: view'
          ) === -1
        ) {
          global.dataLayer.push({
            event: 'sticky_request_quote',
            event_category: 'find a contractor',
            event_action: 'sticky request a quote: view',
            event_attributes: {
              contractor_type: 'residential, commercial, undefined'
            }
          });
        }
      }
    } else {
      setSticky(false);
      if (sticky) {
        card.current?.parentElement?.classList.remove(
          'contractor-request-a-quote-form--sticky'
        );
      }
    }
  };

  const handleResize = () => {
    const header = (card.current as HTMLElement).querySelector(
      '.request-a-quote__header'
    );
    const footer = (card.current as HTMLElement).querySelector(
      '.request-a-quote__footer'
    );

    const cardFixed = (card.current as HTMLElement).querySelector(
      '.request-a-quote__card'
    );

    setFixedHeight(
      (header as HTMLElement)?.offsetHeight +
        (footer as HTMLElement)?.offsetHeight
    );

    if (!expanded) {
      setCardHeight((cardFixed as HTMLElement)?.offsetHeight);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      clearAllBodyScrollLocks();
    };
  }, []);

  const handleExpandButton = () => {
    setExpanded(prev => !prev);

    const cardFixed = (card.current as HTMLElement).querySelector(
      '.request-a-quote__card'
    );
    if (!expanded) {
      setCardHeight((cardFixed as HTMLElement)?.offsetHeight);
      disableBodyScroll(card.current as HTMLDivElement);
    } else {
      clearAllBodyScrollLocks();
    }
  };

  if ((contractorCard as string).trim() === 'true') {
    return (
      <div
        ref={card}
        style={styleRequestAQuote}
        className={`request-a-quote__card-row row ${
          sticky ? 'request-a-quote__card-row--sticky ' : ''
        }${
          expanded && isTabletLandscape ? 'request-a-quote__card-row--open' : ''
        }`}
      >
        <div className="request-a-quote__card-wrapper">
          <div className="request-a-quote__card">
            <Card {...callUsCardLabels} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {sticky && isTabletLandscape && (
        <button
          onClick={() => handleExpandButton()}
          className={`request-a-quote__expand-btn ${
            expanded ? 'request-a-quote__expand-btn--open' : ''
          }`}
          data-visible-layer={createDataLayerString({
            event_attributes: {
              contractor_id: contractorData.id,
              contractor_name: contractorData.contractorName,
              contractor_certificates_count:
                contractorData.numberOfCertificates,
              contractor_rating: contractorData.rating,
              contractor_reviews_count: contractorData.numberOfReviews,
              contractor_type: contractorData.type,
              contractor_certificate_name: contractorData.certificateName
            },
            event: 'sticky_request_quote',
            event_category: 'find a contractor',
            event_action: 'sticky request a quote: view',
            event_label: ''
          })}
          data-layer={createDataLayerString([
            {
              event_attributes: {
                contractor_id: contractorData.id,
                contractor_name: contractorData.contractorName,
                contractor_certificates_count:
                  contractorData.numberOfCertificates,
                contractor_rating: contractorData.rating,
                contractor_reviews_count: contractorData.numberOfReviews,
                contractor_type: contractorData.type,
                contractor_certificate_name: contractorData.certificateName
              },
              event: 'sticky_request_quote',
              event_category: 'find a contractor',
              event_action: 'sticky request a quote: click',
              event_label: ''
            }
          ])}
        >
          {heading}
        </button>
      )}
      <div
        ref={card}
        style={styleRequestAQuote}
        className={`request-a-quote__card-row row ${
          sticky ? 'request-a-quote__card-row--sticky ' : ''
        }${
          expanded && isTabletLandscape ? 'request-a-quote__card-row--open' : ''
        }`}
      >
        <div className="request-a-quote__card-wrapper">
          <div className="request-a-quote__card">
            <RequestAQuoteForm
              {...props}
              contractor={contractorData}
              formLabels={formLabels}
            ></RequestAQuoteForm>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContractorRequestAForm;
